import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { ResourceVm } from 'qp-ui-interface';
import { ClevertapEvent } from 'src/app/analytics/clevertap/enum/clevertap.eventType';
import { ClevertapService } from 'src/app/analytics/clevertap/service/clevertap.service';
import { AppEventName } from 'src/app/analytics/newrelic/enums/EventName';
import { PageType } from 'src/app/analytics/newrelic/enums/PageType';
import { NewrelicService } from 'src/app/analytics/newrelic/services/newrelic.service';
import { NewrelicAppStore } from 'src/app/analytics/newrelic/store/newrelicApp.store';
import { DataService } from 'src/app/data.service';
import { LocalStorageKeys } from 'src/app/shared/LocalStorageKeys';
import { NavigationLibraryService } from 'src/app/shared/services/navigation/navigation.service';
import { FocusRestorationService } from 'src/app/shared/services/utility/focus-restoration.service';
import { CARD_ASPECT_RATIO } from 'src/app/shared/utils/cards/card.constants';
import { CardsService } from '../card/cards.service';
import { DialogService } from 'src/app/shared/services/dialog.service';

export interface FocusedGridEvent {
  index: number;
  resource: ResourceVm;
  cardsPerRow: number;
}

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
})
export class GridComponent implements OnInit {
  // View References
  @ViewChildren('row') rowRefs!: QueryList<ElementRef<HTMLDivElement>>;

  // Input Properties
  @Input() data: ResourceVm[] = [];
  @Input() aspectRatio: string = '2x3';
  @Input() showHeader: boolean = true;
  @Input() header: string = '';
  @Input() setMargin: boolean = true;
  @Input() canUpdateCurrentResource: boolean = false;
  @Input() isSearchPage: boolean = false;
  @Input() shouldReuse: boolean = false;

  // Output Events
  @Output() focusUp = new EventEmitter<void>();
  @Output() focusRight = new EventEmitter<void>();
  @Output() focusDown = new EventEmitter<void>();
  @Output() focusLeft = new EventEmitter<void>();
  @Output() isFocused = new EventEmitter<ResourceVm>();
  @Output() clicked = new EventEmitter<ResourceVm>();

  imageLoaded = false;

  // Grid Layout Properties
  private _carouselWidth: number = 0;
  private _noOfCardsShown: number = 5;
  private _carouselGap: number = 0;
  private _carouselGapInVw: string = '';
  private _sectionWidth: string = '';
  private _cardWidth: string = '';
  private _cardHeight: string = '';
  private _gridRows: ResourceVm[][] = [];
  private _cachedClassNames: string = '';
  private _lastAspectRatio: string = '';
  private focusDebounceTimer: any = null;
  private clickDebounceTimer: any = null;
  private isClickDisabled: boolean = false;
  private readonly CLICK_DEBOUNCE_DELAY: number = 1500; // 200ms debounce for smart TV compatibility

  // Getters for optimized access
  get carouselWidth(): number {
    return this._carouselWidth;
  }
  get noOfCardsShown(): number {
    return this._noOfCardsShown;
  }
  get carouselGap(): number {
    return this._carouselGap;
  }
  get carouselGapInVw(): string {
    return this._carouselGapInVw;
  }
  get sectionWidth(): string {
    return this._sectionWidth;
  }
  get cardWidth(): string {
    return this._cardWidth;
  }
  get gridRows(): ResourceVm[][] {
    return this._gridRows;
  }
  get lastAspectRatio(): string {
    return this._lastAspectRatio;
  }
  get cardHeight(): string {
    return this._cardHeight;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private cardsService: CardsService,
    private readonly dataService: DataService,
    private readonly router: Router,
    private navService: NavigationLibraryService,
    private readonly clevertapService: ClevertapService,
    private readonly newrelic: NewrelicService,
    private readonly newrelicStore: NewrelicAppStore,
    private readonly focusRestorationService: FocusRestorationService,
    private readonly dialogService: DialogService
  ) {}

  // ================ Lifecycle Hooks ================

  ngOnInit(): void {
    this.updateGridRows();
    this._carouselGapInVw = this.cardsService.vwConversion(this._carouselGap);
    this._noOfCardsShown = this.getNoOfCardsShown();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['aspectRatio'] || changes['data']) {
      this.updateGridRows();
    }
    if (changes['aspectRatio']) {
      this._lastAspectRatio = changes['aspectRatio']?.currentValue;
      this._cachedClassNames = ''; // Reset cache when aspect ratio changes
    }

    // Reset click state when data changes (component being reused with new data)
    if (changes['data'] && !changes['data'].firstChange) {
      this.resetClickState();
    }

    this._noOfCardsShown = this.getNoOfCardsShown();
    this.updateCarouselWidth();
  }

  ngAfterViewInit(): void {
    this.updateCarouselWidth();
  }

  onClick(resource: ResourceVm): void {
    // Debouncer - prevent rapid successive clicks
    if (this.isClickDisabled || this.dialogService.hasOpenDialog()) {
      return; // Ignore rapid successive clicks
    }

    // Disable further clicks immediately
    this.isClickDisabled = true;

    // Set timer to re-enable clicks after debounce delay
    this.clickDebounceTimer = setTimeout(() => {
      this.isClickDisabled = false;
      this.clearClickDebounceTimer();
    }, this.CLICK_DEBOUNCE_DELAY);

    this.onClickAction(resource);
  }

  private onClickAction(resource: ResourceVm): void {
    this.clicked.emit(resource);
    const position =
      this.data?.findIndex((item) => item.id === resource.id) + 1; // +1 index is 0 based
    const searchItemPosition = this.isSearchPage ? position : undefined;
    if (searchItemPosition) {
      resource.ex_id = String(searchItemPosition);
    } else {
      resource.ex_id = null;
    }

    this.dataService.selectedCardData = resource;
    localStorage.removeItem(LocalStorageKeys.TAB_CHANGED);
    // Save the current focus position before navigation if reuse is enabled
    if (this.shouldReuse) {
      this.dataService.gridFocusPosition = {
        verticalIndex: this.navService.getVerticalIndex(),
        horizontalIndex: this.navService.getHorizontalIndex(),
      };
    }

    const { type, id: contentID } = resource;

    const playerTypes = [
      'liveevent',
      'livechannel',
      'channel',
      'shortvideo',
      'trailer',
    ];
    if (playerTypes.includes(resource.type)) {
      this.router.navigate([`player/${type}/${contentID}`], {
        state: { type, id: contentID, analyticsResource: resource },
      });
    } else {
      // const pageID = this.isSearchPage ? PageType.Search : PageType.Collection;
      // this.newrelic.sendAppEvent(AppEventName.VIEW_CONTENT_DETAILS, {
      //   ...this.newrelicStore.getContentDetails(
      //     resource,
      //     pageID,
      //     searchItemPosition
      //   ),
      // });
      // this.clevertapService.addEvent(ClevertapEvent.CONTENT_CLICKED, {
      //   resource: resource,
      // });
      this.router.navigate([`/${resource.contentType}/${resource.uniqueId}`]);
    }
  }

  gridData(): ResourceVm[][] {
    return this._gridRows;
  }

  onImageError(event: Event): void {
    const fallback = '/assets/icons/placeholder_no_image.svg';
    const img = event.target as HTMLImageElement;

    if (img && img.src !== fallback) {
      img.src = fallback;
    }
  }

  getClassNames(): string {
    if (!this._cachedClassNames) {
      this._cachedClassNames =
        CARD_ASPECT_RATIO.find((card) =>
          this.aspectRatio.includes(card.aspectRatio)
        )?.className || '';
    }
    return this._cachedClassNames;
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  onKeyDown(
    event: KeyboardEvent,
    verticalIndex: number,
    horizontalIndex: number
  ): void {
    if (event.isUp()) {
      if (verticalIndex === 0) {
        this.focusUp.emit();
      } else {
        this.scrollToRow(verticalIndex - 1);
      }
    } else if (event.isDown()) {
      if (verticalIndex === this.gridData().length - 1) {
        this.focusDown.emit();
      } else {
        this.scrollToRow(verticalIndex + 1);
      }
    } else if (event.isRight()) {
      const rowLength = this.gridData()[verticalIndex].length;
      if (horizontalIndex === rowLength - 1) {
        this.focusRight.emit();
        event.stopPropagation();
      }
    } else if (event.isLeft()) {
      if (horizontalIndex === 0) {
        this.focusLeft.emit();
        event.stopPropagation();
      }
    }
  }

  onFocus(
    resource: ResourceVm,
    verticalIndex: number,
    horizontalIndex: number
  ): void {
    this.clearFocusCardDebounceTimer();

    this.focusDebounceTimer = setTimeout(() => {
      if (this.canUpdateCurrentResource) {
        this.dataService.selectedCardData = resource;
      }
      this.isFocused.emit(resource);

      // Save the current focus position if reuse is enabled
      if (this.shouldReuse) {
        this.dataService.gridFocusPosition = {
          verticalIndex,
          horizontalIndex,
        };
      }
      this.clearFocusCardDebounceTimer();
    }, 240);
  }

  scrollToRow(verticalIndex: number): void {
    this.focusRestorationService.scrollToRow(verticalIndex);
  }

  onImageLoad() {
    this.imageLoaded = true;
  }

  // ================ Private Methods ================

  private updateGridRows(): void {
    const noOfCardsShown = this.getNoOfCardsShown();
    this._gridRows = [];
    for (let i = 0; i < this.data.length; i += noOfCardsShown) {
      this._gridRows.push(this.data.slice(i, i + noOfCardsShown));
    }
  }

  private getNoOfCardsShown(): number {
    const cardsPerRatio: { [key: string]: number } = {
      '2x3': 7,
      '9x16': 6,
      '16x9': 5,
    };
    return cardsPerRatio[this.aspectRatio] || 7;
  }

  private updateCarouselWidth(): void {
    requestAnimationFrame(() => {
      const firstRow = this.rowRefs.first?.nativeElement;
      if (!firstRow) return;

      const newWidth = firstRow.offsetWidth;
      if (this._carouselWidth !== newWidth) {
        this._carouselWidth = newWidth;
      }
      this.calculateWidth();
      this.cdr.detectChanges();
    });
  }

  private calculateWidth(): void {
    const { sectionWidth, cardWidth } = this.cardsService.calculateCardWidth(
      this._carouselWidth,
      this._carouselGap,
      this._noOfCardsShown,
      0.312,
      0.521 // Border thickness 6px if you adjust in css you need change it here as well
    );
    this._sectionWidth = `${sectionWidth}px`;
    this._cardWidth = `${cardWidth}px`;
    this._cardHeight = `${this.cardsService.calculateCardHeight(
      cardWidth,
      this.aspectRatio
    )}px`;
  }

  ngOnDestroy(): void {
    this.clearFocusCardDebounceTimer();
    this.clearClickDebounceTimer();
  }

  clearFocusCardDebounceTimer() {
    if (this.focusDebounceTimer) {
      clearTimeout(this.focusDebounceTimer);
      this.focusDebounceTimer = null;
    }
  }

  private clearClickDebounceTimer(): void {
    if (this.clickDebounceTimer) {
      clearTimeout(this.clickDebounceTimer);
      this.clickDebounceTimer = null;
    }
  }

  private resetClickState(): void {
    this.isClickDisabled = false;
    this.clearClickDebounceTimer();
  }
}
